import { Controller } from "@hotwired/stimulus";
import { insertRowFromTemplate, removeRow, updateSortKeys } from "../../helpers/row_list_helpers";
import { EDITABLE_IMAGE_TYPES } from "../../helpers/image_helpers";

const T = (name) => `[data-admin--checkout-form-products-target='${name}']`;

export default class extends Controller {
  static targets = [
    "list",
    "template",
    "productRow",
    "sortKey",
    "destroyInput",
    "foreignKeySelect",
    "foreignKeyField",
    "priceField",
    "taxRateField",
    "productTypeHidden",
    "typeHidden",
    "isMembershipHidden",
    "existingMembersSection",
    "existingMembersHidden",
    "existingMemberForeignKey",
    "existingMemberPriceField",
    "scheduledSwitchSection",
    "scheduledSwitchHidden",
    "switchProductSelect",
    "switchPriceField",
    "timingTypeSelect",
    "daysToWaitField",
    "rechargesToWaitField",
    "giftCardValueSection",
    "giftCardValueHidden",
    "salespathVoucherSection",
    "salespathVoucherHidden",
    "voucherSection",
    "voucherHidden",
    "deliveryUrlField",
    "removeButton",
    "nameInput",
    "addImageLabel",
    "imagePreview",
    "imageInput",
    "imageRemoveFlag",
    "imagePreviewUrlHidden",
    "imagePreviewTemplate",
    "imageEditOverlay",
    "settingsMenu",
  ];

  static values = { nextIndex: Number };

  connect() {
    this._index = this.nextIndexValue || 0;
    this._updateRemoveButtonVisibility();
    this._syncSectionInputStates();
  }

  get nextIndex() {
    return this._index++;
  }

  addProduct(event) {
    event.preventDefault();
    insertRowFromTemplate(this.listTarget, this.templateTarget, this.nextIndex);
    this.updateSortKeys();
    this._updateRemoveButtonVisibility();
    this._repopulateProductSelects();
    this._markDirty();
  }

  removeProduct(event) {
    const row = event.target.closest(T("productRow"));
    if (!row) return;

    removeRow(row, T("destroyInput"));
    this.updateSortKeys();
    this._updateRemoveButtonVisibility();
    this._markDirty();
  }

  openSettings(event) {
    const el = event.currentTarget;
    const menu =
      el.querySelector(T("settingsMenu")) || el.parentElement.querySelector(T("settingsMenu"));
    if (menu) menu.classList.remove("hidden");
  }

  closeSettings(event) {
    const el = event.currentTarget;
    const menu =
      el.querySelector(T("settingsMenu")) || el.parentElement.querySelector(T("settingsMenu"));
    if (menu) menu.classList.add("hidden");
  }

  toggleSettings(event) {
    event.stopPropagation();
    const el = event.currentTarget;
    const menu = el.closest(".tooltip")?.querySelector(T("settingsMenu"));
    if (!menu) return;

    menu.classList.remove("hidden");

    // Close on click outside (for touch/click-only flows where
    // mouseleave doesn't fire)
    const close = (e) => {
      if (!menu.contains(e.target) && !el.contains(e.target)) {
        menu.classList.add("hidden");
        document.removeEventListener("click", close);
      }
    };
    document.addEventListener("click", close);
  }

  toggleExistingMembers(event) {
    this._toggleSection(event, "existingMembersSection", "existingMembersHidden");
    this._updateBlockSaleMessage();
  }

  existingMemberProductChanged(event) {
    const row = this.findProductRow(event);
    const priceWrap = row?.querySelector(T("existingMemberPriceField"));
    if (priceWrap) priceWrap.classList.toggle("hidden", !event.target.value);
    this._updateBlockSaleMessage();
  }

  toggleScheduledSwitch(event) {
    this._toggleSection(event, "scheduledSwitchSection", "scheduledSwitchHidden");
  }

  toggleGiftCardValue(event) {
    this._toggleSection(event, "giftCardValueSection", "giftCardValueHidden");
  }

  toggleSalespathVoucher(event) {
    this._toggleSection(event, "salespathVoucherSection", "salespathVoucherHidden");
  }

  toggleVoucher(event) {
    this._toggleSection(event, "voucherSection", "voucherHidden");
  }

  timingTypeChanged(event) {
    const row = this.findProductRow(event);
    if (!row) return;

    const timingType = event.target.value;
    const daysField = row.querySelector(T("daysToWaitField"));
    const rechargesField = row.querySelector(T("rechargesToWaitField"));

    if (daysField && rechargesField) {
      const showRecharges = timingType && timingType.startsWith("recharges");
      daysField.classList.toggle("hidden", showRecharges);
      rechargesField.classList.toggle("hidden", !showRecharges);
    }
  }

  productChanged(event) {
    const row = this.findProductRow(event);
    if (!row) return;

    const editor = this._editorController;
    if (!editor) return;

    const foreignKey = event.target.value;
    const product = editor
      .getAvailableProducts()
      .find((p) => p.foreign_key?.toString() === foreignKey?.toString());

    const typeHidden = row.querySelector(T("typeHidden"));
    const isMembershipHidden = row.querySelector(T("isMembershipHidden"));
    const productTypeHidden = row.querySelector(T("productTypeHidden"));
    // A Nexus gift card's value comes from its sellable via the picker; the
    // hidden input carries it so the admin never types one that could drift.
    const giftCardValueHiddenDiv = row.querySelector(T("giftCardValueHidden"));
    const giftCardValueHidden = giftCardValueHiddenDiv?.querySelector("input");

    if (product) {
      if (typeHidden) typeHidden.value = product.type || "";
      if (isMembershipHidden) isMembershipHidden.value = product.is_membership || false;
      if (productTypeHidden) productTypeHidden.value = product.product_type || "";
      if (giftCardValueHidden) giftCardValueHidden.value = product.gift_card_value || "";
      this._applyPosDetails(row, product);
    } else {
      if (typeHidden) typeHidden.value = "";
      if (isMembershipHidden) isMembershipHidden.value = "";
      if (productTypeHidden) productTypeHidden.value = "";
      if (giftCardValueHidden) giftCardValueHidden.value = "";
    }

    // Only a WashConnect gift card takes a hand-typed value. If the section
    // was open for one and the row now holds a different product, close it —
    // classes and disabled states both, mirroring _toggleSection — so the
    // stale editable input can't outrank the hidden (sourced) value.
    const isWcGiftCard =
      product?.type === "WashConnectProduct" && product?.product_type === "GiftCard";
    const giftCardValueSection = row.querySelector(T("giftCardValueSection"));
    if (!isWcGiftCard && giftCardValueSection && giftCardValueHiddenDiv) {
      giftCardValueSection.classList.add("hidden");
      giftCardValueHiddenDiv.classList.remove("hidden");
      giftCardValueSection.querySelectorAll("input, select, textarea").forEach((el) => {
        el.disabled = true;
      });
      giftCardValueHiddenDiv.querySelectorAll("input, select").forEach((el) => {
        el.disabled = false;
      });
    }

    this._updateProductSettingsMenuItems(row, product);
  }

  updateSortKeys() {
    updateSortKeys(this.listTarget, T("productRow"), T("sortKey"));
  }

  findProductRow(event) {
    return event.target.closest(T("productRow"));
  }

  imageSelected(event) {
    const row = this.findProductRow(event);
    if (!row) return;

    const file = event.target.files[0];
    const preview = row.querySelector(T("imagePreview"));
    const template = row.querySelector(T("imagePreviewTemplate"));
    if (!preview || !template) return;

    const addLabel = row.querySelector(T("addImageLabel"));

    if (file) {
      const blobUrl = URL.createObjectURL(file);
      preview.innerHTML = template.innerHTML;
      delete preview.dataset.posSourced;
      const img = preview.querySelector("[data-image-preview]");
      if (img) img.src = blobUrl;
      if (addLabel) addLabel.classList.add("hidden");

      this._setImagePreviewUrl(row, blobUrl);
      this._toggleImageEditable(row, EDITABLE_IMAGE_TYPES.includes(file.type));
    }

    const removeFlag = row.querySelector(T("imageRemoveFlag"));
    if (removeFlag) removeFlag.value = "";
  }

  removeImage(event) {
    event.preventDefault();
    const row = this.findProductRow(event);
    if (!row) return;

    const preview = row.querySelector(T("imagePreview"));
    // Removing a preview we sourced rejects that sellable's image, not the product's — the flag has
    // to lift when the row is mapped somewhere else.
    const removedPosPreview = preview?.dataset.posSourced === "true";
    if (preview) {
      preview.innerHTML = "";
      delete preview.dataset.posSourced;
    }

    const addLabel = row.querySelector(T("addImageLabel"));
    if (addLabel) addLabel.classList.remove("hidden");

    const input = row.querySelector(T("imageInput"));
    if (input) input.value = "";

    const removeFlag = row.querySelector(T("imageRemoveFlag"));
    if (removeFlag) {
      removeFlag.value = "1";
      if (removedPosPreview) {
        removeFlag.dataset.posRemoval = "true";
      } else {
        delete removeFlag.dataset.posRemoval;
      }
    }

    this._setImagePreviewUrl(row, "");
    this._toggleImageEditable(row, false);
    this._markDirty();
  }

  // Typing marks the name as the admin's, so re-picking a product stops replacing it.
  nameEdited(event) {
    delete event.target.dataset.posFilled;
  }

  // Fills in what the POS already knows without overwriting the admin. Values this controller
  // filled itself stay marked, so switching to another sellable replaces them rather than leaving
  // the previous one's name and image on the new product.
  _applyPosDetails(row, product) {
    this._applyPosName(row, product);
    this._applyPosImage(row, product);
  }

  _applyPosName(row, product) {
    const nameInput = row.querySelector(T("nameInput"));
    if (!nameInput) return;
    if (nameInput.value.trim() && nameInput.dataset.posFilled !== "true") return;

    // A POS that sends no name clears ours rather than stranding the last one's — switching the
    // replication group leaves the row's name behind while resetting its selection.
    if (product.pos_name) {
      nameInput.value = product.pos_name;
      nameInput.dataset.posFilled = "true";
    } else {
      nameInput.value = "";
      delete nameInput.dataset.posFilled;
    }
  }

  _applyPosImage(row, product) {
    const preview = row.querySelector(T("imagePreview"));
    const template = row.querySelector(T("imagePreviewTemplate"));
    if (!preview || !template) return;

    const removeFlag = row.querySelector(T("imageRemoveFlag"));
    if (removeFlag?.value === "1") {
      // A removal that rejected a POS preview applied to the previous mapping only; one that
      // rejected the product's own image stands, and suppresses this mapping's import too.
      if (removeFlag.dataset.posRemoval !== "true") return;

      removeFlag.value = "";
      delete removeFlag.dataset.posRemoval;
    }

    // Anything already on the row that this controller did not put there is the admin's — either
    // an upload in progress or an image saved earlier. Leave it; the server decides whether a
    // remap replaces a saved one.
    const posSourced = preview.dataset.posSourced === "true";
    if (preview.innerHTML.trim() !== "" && !posSourced) return;

    if (!product.image_url) {
      if (posSourced) this._clearPosImage(row, preview);
      return;
    }

    preview.innerHTML = template.innerHTML;
    preview.dataset.posSourced = "true";
    const img = preview.querySelector("[data-image-preview]");
    if (img) img.src = product.image_url;

    const addLabel = row.querySelector(T("addImageLabel"));
    if (addLabel) addLabel.classList.add("hidden");

    this._setImagePreviewUrl(row, product.image_url);
    // POS-sourced previews are remote URLs — cross-origin, so the canvas editor can't open them.
    this._toggleImageEditable(row, false);
  }

  _clearPosImage(row, preview) {
    preview.innerHTML = "";
    delete preview.dataset.posSourced;

    const addLabel = row.querySelector(T("addImageLabel"));
    if (addLabel) addLabel.classList.remove("hidden");

    this._setImagePreviewUrl(row, "");
    this._toggleImageEditable(row, false);
  }

  // Turning a row non-editable also drops the persisted image's editor dataset from the preview
  // container: once the preview shows something else (a POS image, a removal), a click must not
  // open the editor against the stale saved image, whose Apply would then replace the new preview.
  _toggleImageEditable(row, editable) {
    const overlay = row.querySelector(T("imageEditOverlay"));
    if (overlay) overlay.classList.toggle("hidden", !editable);

    if (!editable) {
      const preview = row.querySelector(T("imagePreview"));
      if (preview) {
        ["imageUrl", "contentType", "filename", "byteSize"].forEach((key) => {
          delete preview.dataset[key];
        });
      }
    }
  }

  _setImagePreviewUrl(row, value) {
    const input = row.querySelector(T("imagePreviewUrlHidden"));
    if (!input) return;
    input.value = value;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  get _editorController() {
    const el = this.element.closest("[data-controller~='admin--checkout-form-editor']");
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(el, "admin--checkout-form-editor");
  }

  _syncSectionInputStates() {
    const pairs = [
      ["existingMembersSection", "existingMembersHidden"],
      ["scheduledSwitchSection", "scheduledSwitchHidden"],
      ["giftCardValueSection", "giftCardValueHidden"],
      ["salespathVoucherSection", "salespathVoucherHidden"],
      ["voucherSection", "voucherHidden"],
    ];

    this.productRowTargets.forEach((row) => {
      pairs.forEach(([sectionTarget, hiddenTarget]) => {
        const section = row.querySelector(T(sectionTarget));
        const hidden = row.querySelector(T(hiddenTarget));
        if (!section || !hidden) return;

        const sectionVisible = !section.classList.contains("hidden");
        hidden.querySelectorAll("input, select").forEach((el) => {
          el.disabled = sectionVisible;
        });
      });
    });
  }

  _toggleSection(event, sectionTarget, hiddenTarget) {
    const row = this.findProductRow(event);
    if (!row) return;

    const section = row.querySelector(T(sectionTarget));
    const hidden = row.querySelector(T(hiddenTarget));
    if (!section || !hidden) return;

    const showingSection = section.classList.contains("hidden");
    section.classList.toggle("hidden", !showingSection);
    hidden.classList.toggle("hidden", showingSection);

    // disable inputs in whichever container is now hidden so they don't submit
    hidden.querySelectorAll("input, select").forEach((el) => {
      el.disabled = showingSection;
    });
    section.querySelectorAll("input, select, textarea").forEach((el) => {
      el.disabled = !showingSection;
    });

    this._markDirty();
  }

  _markDirty() {
    this._editorController?.markDirty();
  }

  _updateRemoveButtonVisibility() {
    const visibleRows = this.listTarget.querySelectorAll(`${T("productRow")}:not(.hidden)`);
    const hide = visibleRows.length <= 1;
    this.removeButtonTargets.forEach((btn) => {
      btn.classList.toggle("invisible", hide);
    });
  }

  _updateProductSettingsMenuItems(row, product) {
    const scheduledSwitchItem = row.querySelector("[data-product-setting='scheduled-switch']");
    if (scheduledSwitchItem) {
      scheduledSwitchItem.classList.toggle("hidden", !product?.is_membership);
    }

    const isWcGiftCard =
      product?.type === "WashConnectProduct" && product?.product_type === "GiftCard";
    const giftCardItem = row.querySelector("[data-product-setting='gift-card-value']");
    if (giftCardItem) {
      giftCardItem.classList.toggle("hidden", !isWcGiftCard);
    }
  }

  _updateBlockSaleMessage() {
    const editor = this._editorController;
    if (!editor) return;

    const rows = this.listTarget.querySelectorAll(`${T("productRow")}:not(.hidden)`);

    let hasExistingMembersSection = false;
    let hasExistingMemberProduct = false;

    rows.forEach((row) => {
      const emSection = row.querySelector(T("existingMembersSection"));
      if (emSection && !emSection.classList.contains("hidden")) {
        hasExistingMembersSection = true;
        const emFk = row.querySelector(T("existingMemberForeignKey"));
        if (emFk?.value) {
          hasExistingMemberProduct = true;
        }
      }
    });

    if (editor.hasExistingMemberConfirmationTarget) {
      editor.existingMemberConfirmationTarget.classList.toggle("hidden", !hasExistingMemberProduct);
    }

    const confirmationChecked =
      editor.hasExistingMemberConfirmationTarget &&
      editor._checkboxChecked(editor.existingMemberConfirmationTarget);

    const showBlockSale =
      hasExistingMembersSection &&
      (!hasExistingMemberProduct || (hasExistingMemberProduct && confirmationChecked));

    if (editor.hasBlockSaleMessageTarget) {
      editor.blockSaleMessageTarget.classList.toggle("hidden", !showBlockSale);
    }
  }

  _repopulateProductSelects() {
    const editor = this._editorController;
    if (!editor) return;
    editor.populateProductSelects();
    // Ensure new product rows match current delivery URL visibility state
    this.deliveryUrlFieldTargets.forEach((el) =>
      el.classList.toggle("hidden", !editor.showDeliveryUrlValue)
    );
    // Ensure new product rows match current tax rate visibility state
    const showTax =
      editor.hasShowTaxRatesCheckboxTarget &&
      editor._checkboxChecked(editor.showTaxRatesCheckboxTarget);
    this.taxRateFieldTargets.forEach((el) => el.classList.toggle("hidden", !showTax));
  }
}
